import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { PeripheralType } from '~/common/models/peripheral.model';

import { ActivationBatchStatus } from '../activationProgress.types';
import { getPresentationGroupKey } from '../batch.utils';

/**
 * A single in-flight (or just-finished) peripheral activation batch tracked
 * globally so the progress widget survives page navigation and can stack
 * multiple concurrent batches.
 */
export type ActivationBatch = {
  /** Batch id returned by the activate call; the polling key. */
  batchId: string;
  /**
   * Org id that started the batch. Polling uses this (not the current org
   * context) so navigating to another org mid-flight doesn't query the wrong
   * org header and mis-report the batch.
   */
  orgId: string;
  /** Product label for the card title (e.g. "OnStar"). */
  productName?: string;
  /**
   * Which card this batch's presentation (title/text/action overrides below)
   * belongs to. Batches that omit it share the default peripheral card;
   * batches from different integrations must use different values so a
   * customized batch can never mislabel, or have its counts mixed into, an
   * unrelated concurrent batch's card.
   */
  presentationGroup?: string;
  /**
   * Pre-translated in-progress title (e.g. "Integrating with Coast"). Caller-
   * defined per batch; falls back to the peripheral "… activation" wording
   * built from {@link ActivationBatch.productName} when omitted.
   */
  progressTitle?: string;
  /** Pre-translated terminal-state title (e.g. "Integration complete"). Same fallback rule as {@link ActivationBatch.progressTitle}. */
  completeTitle?: string;
  /**
   * Label for an optional button on the completion card (e.g. "View details").
   * Its callback is resolved from the handler registry using `presentationGroup`.
   */
  primaryActionLabel?: string;
  /**
   * Peripheral type of the batch (e.g. "fleet-camera" / "adapter"). Optional
   * metadata retained so a retry can rebuild the activate request for the
   * batch's devices. Not set by the legacy start-activation path.
   */
  peripheralType?: PeripheralType;
  /** Fleet the batch was started under, sent as the `x-bd-fleet` header on retry. */
  fleetId?: string;
  /** Peripheral ids originally submitted for this batch (retry target). */
  peripheralIds?: string[];
  /**
   * Ids of the devices that failed activation, captured from the batch-status
   * response (`details.errors[].deviceId`). The bulk "try again" retry targets
   * these when known.
   */
  failedPeripheralIds?: string[];
  /**
   * Vehicles enrolled alongside this batch in the same OST enroll action
   * (Shop Add-Ons). Set only for the mixed vehicles + adapters enroll so the
   * completion summary reports both in one place (a single combined toast, or
   * an extra "vehicles enrolled" row in the bulk completion card) instead of a
   * separate immediate enroll toast. Absent for every other activation path.
   */
  enrolledVehicleCount?: number;
  /**
   * Items in the same activation that were *re-enrollments* (already-enrolled
   * devices still in term). Re-enrollment is billing-only — no vendor activation
   * runs for them — so they are never part of {@link ActivationBatch.total} and
   * their outcome comes from the order status rather than the polled batch.
   */
  reenrollmentCount?: number;
  /**
   * Whether the billing order reported a failure status for the re-enrolled
   * items. Meaningful only alongside {@link ActivationBatch.reenrollmentCount}.
   */
  reenrollmentFailed?: boolean;
  /** Total devices in the batch. */
  total: number;
  /** Devices activated so far. */
  success: number;
  /** Devices that failed so far. */
  failure: number;
  status: ActivationBatchStatus;
  /** Epoch ms the batch started, for the "Started at …" row. */
  startedAt: number;
};

/**
 * Caller-supplied fields for a brand-new batch: everything except the values
 * that are fixed for any batch at creation time (counts, terminal status, and
 * the start timestamp), which {@link createActivationBatch} fills in.
 */
export type NewActivationBatchInput = Omit<
  ActivationBatch,
  'success' | 'failure' | 'status' | 'startedAt'
>;

/**
 * Builds a fresh in-progress {@link ActivationBatch} from the caller's seed
 * fields, filling the invariant defaults (zero counts, IN_PROGRESS, started
 * now). Single source of truth for "a new batch" so the initial-activation,
 * row-retry, and bulk-retry paths can't drift as the batch shape evolves.
 */
export const createActivationBatch = (
  input: NewActivationBatchInput
): ActivationBatch => ({
  ...input,
  success: 0,
  failure: 0,
  status: ActivationBatchStatus.IN_PROGRESS,
  startedAt: Date.now(),
});

/**
 * Builds an already-completed batch for a *successful* activation that was only
 * re-enrollments. Those are settled entirely on the billing side, so the order
 * returns no `batchId` and there is nothing to poll — the batch exists purely so
 * the completion card can report the outcome through the same aggregation as
 * every other batch. It carries no `peripheralIds`, so it is never retryable, and
 * the poller never touches it (it only runs for in-progress batches).
 *
 * Success-only by design: a failed billing order produces no batch at all, since
 * the caller reports it inline (the modal's error banner) or as a wholesale
 * failure toast.
 */
export const createReenrollmentBatch = (
  input: Omit<
    NewActivationBatchInput,
    'batchId' | 'total' | 'peripheralIds' | 'failedPeripheralIds'
  > & { reenrollmentCount: number }
): ActivationBatch => ({
  ...input,
  // Unique per activation: `addActivationBatch` dedupes by id, so a timestamp
  // alone would let a second same-millisecond batch be dropped silently.
  batchId: `reenrollment-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  total: 0,
  success: 0,
  failure: 0,
  status: ActivationBatchStatus.COMPLETED,
  startedAt: Date.now(),
});

export type ActivationProgressState = {
  /** Active batches, one row per batch in the widget. */
  batches: ActivationBatch[];
  /**
   * Bumped whenever a batch reaches a terminal state. Device lists watch this
   * to refetch (the generated peripherals API has no cache tags to invalidate).
   */
  lastCompletedAt?: number;
};

export const INITIAL_ACTIVATION_PROGRESS_STATE: ActivationProgressState = {
  batches: [],
  lastCompletedAt: undefined,
};

const activationProgressSlice = createSlice({
  name: 'activationProgress',
  initialState: INITIAL_ACTIVATION_PROGRESS_STATE,
  reducers: {
    addActivationBatch: (state, action: PayloadAction<ActivationBatch>) => {
      const alreadyTracked = state.batches.some(
        (batch) => batch.batchId === action.payload.batchId
      );
      if (alreadyTracked) return;
      // Drop this presentation group's finished batches so a new activation
      // starts a fresh completion card, without dismissing an unrelated
      // group's still-visible one. Compared via the normalized key (not the
      // raw field) so this can't disagree with the snackbar's own grouping
      // about which batches share a card.
      const newGroupKey = getPresentationGroupKey(action.payload);
      state.batches = state.batches.filter(
        (batch) =>
          batch.status === ActivationBatchStatus.IN_PROGRESS ||
          getPresentationGroupKey(batch) !== newGroupKey
      );
      state.batches.push(action.payload);
    },
    updateActivationBatch: (
      state,
      action: PayloadAction<{
        batchId: string;
        changes: Partial<Omit<ActivationBatch, 'batchId'>>;
      }>
    ) => {
      const batch = state.batches.find(
        (item) => item.batchId === action.payload.batchId
      );
      if (batch) {
        Object.assign(batch, action.payload.changes);
      }
    },
    removeActivationBatch: (state, action: PayloadAction<string>) => {
      state.batches = state.batches.filter(
        (batch) => batch.batchId !== action.payload
      );
    },
    /**
     * Atomically swaps the successfully-retried terminal batches for their new
     * in-progress batches, leaving every other batch untouched. Used by the
     * "try again" flow instead of {@link addActivationBatch}, which clears
     * finished batches in the retried batch's presentation group. With multiple
     * failed batches on the completion card, a per-success `addActivationBatch`
     * would wipe sibling failures whose retry is still in flight or about to
     * fail, hiding them from the widget.
     */
    replaceRetriedBatches: (
      state,
      action: PayloadAction<{
        retiredBatchIds: string[];
        newBatches: ActivationBatch[];
      }>
    ) => {
      const { retiredBatchIds, newBatches } = action.payload;
      state.batches = state.batches.filter(
        (batch) => !retiredBatchIds.includes(batch.batchId)
      );
      for (const batch of newBatches) {
        if (!state.batches.some((item) => item.batchId === batch.batchId)) {
          state.batches.push(batch);
        }
      }
    },
    markActivationCompleted: (state) => {
      state.lastCompletedAt = Date.now();
    },
  },
});

export const {
  addActivationBatch,
  updateActivationBatch,
  removeActivationBatch,
  replaceRetriedBatches,
  markActivationCompleted,
} = activationProgressSlice.actions;

export const activationProgressReducer = activationProgressSlice.reducer;
